package top.wcpe.allincore.dsl.compiler.semantic

import top.wcpe.allincore.dsl.compiler.ast.ExprAst
import top.wcpe.allincore.dsl.compiler.ast.LValueAst
import top.wcpe.allincore.dsl.compiler.ast.StmtAst
import top.wcpe.allincore.dsl.compiler.ast.TypeAst
import top.wcpe.allincore.dsl.compiler.ast.displayName
import top.wcpe.allincore.dsl.compiler.diag.DiagnosticCollector
import top.wcpe.allincore.dsl.compiler.diag.DslError
import top.wcpe.allincore.dsl.compiler.type.IrType
import top.wcpe.allincore.dsl.compiler.type.TypeInferencer
import top.wcpe.allincore.dsl.compiler.type.TypeResolver

/**
 * M10 Stage 1：拆分自原 TypeConstraintChecker。
 *
 * 维度：局部 var 与 fun 形参 / for 迭代变量相关诊断。
 * 涉及诊断码：E620 / E621 / E660 / E662 / E670。
 *
 * 对应原文件方法：
 * - checkVarDeclAnnotation
 * - 未标注 var 登记 inferredVarTypes
 * - checkLocalVarReassignment
 * - checkParamReassign
 * - checkForLoopVarReassign
 * - checkLocalVarTypeDrift
 */
internal class LocalVarTypeChecker(
    private val diag: DiagnosticCollector,
    private val env: VarTypeEnv,
) {

    /** 单条 var 声明：解析标注类型 → 推断 rhs 类型 → isAssignableTo 判定。 */
    fun onVarDecl(name: String, annotation: TypeAst, initializer: ExprAst) {
        val declared = TypeResolver.resolve(annotation)
        if (declared == null) {
            // M6-a：data 类型名由 DataLiteralChecker 单独处理（E748）；TypeConstraint 阶段静默放行。
            // M10 Stage 4：把同文件 / 跨文件 data 类型标注登记为 DataInstance，让 NullSafetyChecker
            // 能在后续 MemberAccess 上反查到 DataInstance 并触发 E669（字段名校验）。
            if (annotation is TypeAst.Named) {
                val dataInstance = resolveDataInstance(annotation)
                if (dataInstance != null) {
                    env.declaredVarTypes[name] = dataInstance
                    return
                }
            }
            // 未知类型：拿 TypeAst 的显示名报 E621
            diag.error(
                DslError.E621_UNKNOWN_TYPE,
                annotation.position,
                annotation.displayName(),
            )
            return
        }
        val actual = TypeInferencer.inferExpr(initializer)
        // M8 阶段 D：登记声明类型供后续二次赋值检查（即便初值检查放行也要登记，
        // 让 `var x: Int = unknownExpr; x = "abc"` 能在第二步抓住错误）
        env.declaredVarTypes[name] = declared
        // 推断不出（AnyT）的场景一律放行：避免对绝大多数旧 .allin 资源产生假阳性
        if (actual == IrType.AnyT) return
        // null 字面量被推断为 Nullable(AnyT)：对任何可空目标都应放行；
        // IrType.isAssignableTo 对 "Nullable(AnyT) -> Nullable(X)" 路径会返回 false（this 为 Nullable 时直接拒），
        // 这里单独特判，避免误报 E620。非空目标仍走下面的 isAssignableTo 逻辑，返回 false 后触发 E620。
        if (actual is IrType.Nullable && actual.inner == IrType.AnyT && declared is IrType.Nullable) {
            return
        }
        if (!actual.isAssignableTo(declared)) {
            diag.error(
                DslError.E620_TYPE_MISMATCH,
                initializer.position,
                declared.displayName(),
                actual.displayName(),
            )
        }
    }

    /**
     * M10 Stage 4：把 [TypeAst.Named] 形态的"data 类型标注"映射为 [IrType.DataInstance]。
     *
     * 命中条件（任一）：
     * 1. 类名在当前 screen 的 [VarTypeEnv.dataTypeNames]（同文件 data 类）
     * 2. 类名在 [VarTypeEnv.crossFileTypes].dataFields（跨文件 data 类）
     *
     * 都未命中时返回 null，调用方按 E621 处理。
     */
    private fun resolveDataInstance(named: TypeAst.Named): IrType? {
        val isLocal = named.name in env.dataTypeNames
        val isCross = env.crossFileTypes.dataFields.containsKey(named.name)
        if (!isLocal && !isCross) return null
        val base = IrType.DataInstance(named.name)
        return if (named.nullable) IrType.Nullable(base) else base
    }

    /**
     * M9 Stage 6 / E662：未标注 var 也登记初值推断类型，供后续二次赋值
     * 跨语句类型 drift 检查使用。AnyT 不登记，避免动态值假阳性。
     */
    fun onUntypedVarDecl(name: String, initializer: ExprAst) {
        val inferred = TypeInferencer.inferExpr(initializer)
        if (inferred != IrType.AnyT) env.inferredVarTypes[name] = inferred
    }

    /**
     * M8 阶段 D：对已标注类型的局部 var 二次赋值做类型一致性校验。
     *
     * 仅当赋值目标 lhs 是 [LValueAst.LocalLValue]、且对应名字已在 declaredVarTypes 内
     * 登记时才触发；rhs 推断为 AnyT 一律放行（与初值阶段策略一致，避免假阳性）。
     *
     * `null` 字面量赋给可空目标的处理与 [onVarDecl] 完全镜像，确保用户看到
     * 一致的诊断行为。
     */
    fun onAssignDeclaredVar(assign: StmtAst.AssignStmt) {
        val lhs = assign.lhs as? LValueAst.LocalLValue ?: return
        val declared = env.declaredVarTypes[lhs.name] ?: return
        val actual = TypeInferencer.inferExpr(assign.rhs)
        if (actual == IrType.AnyT) return
        if (actual is IrType.Nullable && actual.inner == IrType.AnyT && declared is IrType.Nullable) {
            return
        }
        if (!actual.isAssignableTo(declared)) {
            diag.error(
                DslError.E620_TYPE_MISMATCH,
                assign.rhs.position,
                declared.displayName(),
                actual.displayName(),
            )
        }
    }

    /**
     * M9 Stage 6 / E660：fun 形参在体内被二次赋值时报 INFO。
     *
     * 命中规则：lhs 为 [LValueAst.LocalLValue] 且名字落在 currentFunParams 中。
     * 形参与同名局部 var 的歧义在解析阶段已由作用域规则避免，这里不再判别。
     */
    fun onAssignParam(assign: StmtAst.AssignStmt) {
        val lhs = assign.lhs as? LValueAst.LocalLValue ?: return
        if (lhs.name !in env.currentFunParams) return
        diag.info(DslError.E660_PARAM_REASSIGN, assign.position, lhs.name)
    }

    /**
     * M9 Stage 6 / E670：for 循环迭代变量在循环体内被赋值时报 INFO。
     *
     * 命中规则：lhs 为 [LValueAst.LocalLValue] 且名字落在 currentForLoopVars 中。
     */
    fun onAssignForLoopVar(assign: StmtAst.AssignStmt) {
        val lhs = assign.lhs as? LValueAst.LocalLValue ?: return
        if (lhs.name !in env.currentForLoopVars) return
        diag.info(DslError.E670_FOR_LOOP_VAR_REASSIGN, assign.position, lhs.name)
    }

    /**
     * M9 Stage 6 / E662：未标注 var 跨语句类型 drift 时报 INFO。
     *
     * 命中规则：
     * - lhs 为 [LValueAst.LocalLValue] 且名字落在 inferredVarTypes 中（未标注 var）
     * - 且**不在** declaredVarTypes 中（已标注 var 走 E620 路径）
     * - rhs 推断类型与登记类型完全不容（双向 isAssignableTo 都失败）
     *
     * AnyT 与 Nullable(AnyT) 一律放行；与 E620 的边界设计保持一致。
     */
    fun onAssignUntypedVar(assign: StmtAst.AssignStmt) {
        val lhs = assign.lhs as? LValueAst.LocalLValue ?: return
        if (lhs.name in env.declaredVarTypes) return
        val previous = env.inferredVarTypes[lhs.name] ?: return
        val actual = TypeInferencer.inferExpr(assign.rhs)
        if (actual.isDynamic()) return
        // 双向兼容才算"类型一致"——既允许窄→宽（actual ⊆ previous）也允许宽→窄（previous ⊆ actual）
        if (actual.isAssignableTo(previous) || previous.isAssignableTo(actual)) return
        diag.info(
            DslError.E662_LOCAL_VAR_TYPE_DRIFT,
            assign.rhs.position,
            lhs.name,
            previous.displayName(),
            actual.displayName(),
        )
    }
}
